import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@appkit4/react-components/button';
import { Input } from '@appkit4/react-components/field';
import { Panel } from '@appkit4/react-components/panel';
import {
    Combobox,
    CalendarPicker,
    Loading,
    InputNumber,
} from '@appkit4/react-components';
import useLocale from '../../../locale/useLocale';
import Breadcrumbs from '../../../components/shared/breadcrumbs/Breadcrumbs';
import './ValidationSettings.scss';
import { AppRoutes } from '../../../Constants';
import useToast from '../../../helpers/useToast';
import useGenericErrorToast from '../../../helpers/useGenericErrorToast';
import {
    usePostV1AdminGetValidationConfig,
    usePostV1AdminUpdateValidationConfig,
    useGetV1DocumentTypeDocumentTypeIdDocumentTypeFields,
} from '../../../api/generated/endpoint';
import {
    GetValidationConfigResponse,
    GetValidationConfigRequest,
    UpdateValidationConfigRequest,
    SwitchCaseConfigRequest,
    DocumentTypeFieldResponse,
    SwitchCaseConfigResponse,
} from '../../../api/generated/models';

const FillOnlineValidationSettings = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { l } = useLocale();
    const showToast = useToast();
    const showGenericErrorToast = useGenericErrorToast();

    const docType = searchParams.get('docType');
    const docTypeId = parseInt(searchParams.get('docTypeId') || '0', 10);
    const fieldName = searchParams.get('fieldName');
    const fieldId = parseInt(searchParams.get('fieldId') || '0', 10);
    const fieldType = searchParams.get('fieldType');
    const processTypeId = parseInt(
        searchParams.get('processTypeId') || '0',
        10
    );

    const [allFields, setAllFields] = useState<DocumentTypeFieldResponse[]>([]);

    const { data: fieldsData } =
        useGetV1DocumentTypeDocumentTypeIdDocumentTypeFields(docTypeId, {
            processTypeId,
        });

    useEffect(() => {
        if (fieldsData) {
            setAllFields(fieldsData.documentTypeFields || []);
        }
    }, [fieldsData]);

    const getValidationTypes = () => {
        switch (fieldType) {
            case 'Text':
            case 'Number':
                return [
                    { value: 'none', label: 'None' },
                    { value: 'simple', label: 'Simple' },
                    { value: 'and', label: 'And Condition' },
                    { value: 'or', label: 'Or Condition' },
                ];
            case 'Date':
                return [
                    { value: 'none', label: 'None' },
                    { value: 'dateRange', label: 'Date Range' },
                    { value: 'simple', label: 'Simple' },
                    { value: 'and', label: 'And Condition' },
                    { value: 'or', label: 'Or Condition' },
                ];
            case 'Checkbox':
                return [
                    { value: 'none', label: 'None' },
                    { value: 'checkbox', label: 'Checkbox' },
                ];
            default:
                return [{ value: 'none', label: 'None' }];
        }
    };

    const [isChanged, setIsChanged] = useState(false);
    const [validationType, setValidationType] = useState('');
    const [simpleRegex, setSimpleRegex] = useState('');
    const [checkboxValidationError, setCheckboxValidationError] = useState('');
    const [dateRangeStart, setDateRangeStart] = useState<Date | null>(
        new Date(Date.now())
    );
    const [dateRangeEnd, setDateRangeEnd] = useState<Date | null>(
        new Date(Date.now())
    );

    const [validationConfig, setValidationConfig] =
        useState<GetValidationConfigResponse>({
            validationTypeId: undefined,
            simpleRegex: null,
            checkboxOptionCount: 0,
            checkboxMinimumSelected: 0,
            checkboxMaximumSelected: 0,
            fromDate: null,
            toDate: null,
            switchCases: null,
        });

    const {
        mutate: getValidationConfig,
        data: validationData,
        isLoading,
    } = usePostV1AdminGetValidationConfig();

    useEffect(() => {
        if (fieldId) {
            const request: GetValidationConfigRequest = {
                documentTypeFieldId: fieldId,
            };
            getValidationConfig({ data: request });
        }
    }, [fieldId, getValidationConfig]);

    useEffect(() => {
        if (validationData) {
            setValidationConfig(validationData);
            if (validationData.validationTypeId === 1) {
                setSimpleRegex(validationData.simpleRegex || '');
            } else if (validationData.validationTypeId === 4) {
                setDateRangeStart(
                    validationData.fromDate
                        ? new Date(validationData.fromDate)
                        : null
                );
                setDateRangeEnd(
                    validationData.toDate
                        ? new Date(validationData.toDate)
                        : null
                );
            } else if (
                validationData.validationTypeId === 2 ||
                validationData.validationTypeId === 3
            ) {
                const newCases: {
                    field: string;
                    conditions: { value: string; thenValue: string }[];
                    elseValue: string;
                }[] = [];

                let currentCase: {
                    caseNumber: number;
                    field: string;
                    conditions: { value: string; thenValue: string }[];
                    elseValue: string;
                } | null = null;

                validationData.switchCases?.forEach(
                    (switchCase: SwitchCaseConfigResponse) => {
                        if (switchCase.caseNumber !== currentCase?.caseNumber) {
                            if (currentCase) {
                                newCases.push(currentCase);
                            }
                            currentCase = {
                                caseNumber: switchCase.caseNumber || 0,
                                field:
                                    allFields.find(
                                        (f) =>
                                            f.fieldId ===
                                            switchCase.dependantFieldId
                                    )?.fieldName || '',
                                conditions: [],
                                elseValue: '',
                            };
                        }

                        if (switchCase.conditionNumber === -1) {
                            if (currentCase) {
                                currentCase.elseValue =
                                    switchCase.fieldMatchesRegex || '';
                            }
                        } else {
                            if (currentCase) {
                                currentCase.conditions.push({
                                    value:
                                        switchCase.dependantMatchesRegex || '',
                                    thenValue:
                                        switchCase.fieldMatchesRegex || '',
                                });
                            }
                        }
                    }
                );

                if (currentCase) {
                    newCases.push(currentCase);
                }

                if (validationData.validationTypeId === 2) {
                    setAndCases(newCases);
                } else {
                    setOrCases(newCases);
                }
            }
            setValidationType(
                mapValidationTypeIdToString(
                    validationData.validationTypeId ?? 0
                )
            );
            // Validate checkbox settings when loading
            if (validationData.validationTypeId === 5) {
                validateCheckboxRange(
                    validationData.checkboxMinimumSelected ?? 0,
                    validationData.checkboxMaximumSelected ?? 0
                );
            }
        }
    }, [validationData, allFields]);

    const mutation = usePostV1AdminUpdateValidationConfig();

    const [andCases, setAndCases] = useState([
        {
            field: '',
            conditions: [{ value: '', thenValue: '' }],
            elseValue: '',
        },
    ]);
    const [orCases, setOrCases] = useState([
        {
            field: '',
            conditions: [{ value: '', thenValue: '' }],
            elseValue: '',
        },
    ]);

    const handleAddAndCase = () => {
        setAndCases([
            ...andCases,
            {
                field: '',
                conditions: [{ value: '', thenValue: '' }],
                elseValue: '',
            },
        ]);
        setIsChanged(true);
    };

    const handleCaseFieldChange = (caseIndex: number, fieldValue: string) => {
        const updatedCases = [...andCases];
        updatedCases[caseIndex].field = fieldValue;
        setAndCases(updatedCases);
        setIsChanged(true);
    };

    const handleAddAndCondition = (caseIndex: number) => {
        const updatedCases = [...andCases];
        updatedCases[caseIndex].conditions.push({ value: '', thenValue: '' });
        setAndCases(updatedCases);
        setIsChanged(true);
    };

    const handleAndConditionChange = (
        caseIndex: number,
        conditionIndex: number,
        field: 'value' | 'thenValue',
        value: string
    ) => {
        const updatedCases = [...andCases];
        updatedCases[caseIndex].conditions[conditionIndex][field] = value;
        setAndCases(updatedCases);
        setIsChanged(true);
    };

    const handleRemoveAndCondition = (
        caseIndex: number,
        conditionIndex: number
    ) => {
        const updatedCases = [...andCases];
        updatedCases[caseIndex].conditions.splice(conditionIndex, 1);
        setAndCases(updatedCases);
        setIsChanged(true);
    };

    const handleRemoveAndCase = (caseIndex: number) => {
        const updatedCases = [...andCases];
        updatedCases.splice(caseIndex, 1);
        setAndCases(updatedCases);
        setIsChanged(true);
    };

    const handleAddOrCase = () => {
        setOrCases([
            ...orCases,
            {
                field: '',
                conditions: [{ value: '', thenValue: '' }],
                elseValue: '',
            },
        ]);
        setIsChanged(true);
    };

    const handleOrCaseFieldChange = (caseIndex: number, fieldValue: string) => {
        const updatedCases = [...orCases];
        updatedCases[caseIndex].field = fieldValue;
        setOrCases(updatedCases);
        setIsChanged(true);
    };

    const handleAddOrCondition = (caseIndex: number) => {
        const updatedCases = [...orCases];
        updatedCases[caseIndex].conditions.push({ value: '', thenValue: '' });
        setOrCases(updatedCases);
        setIsChanged(true);
    };

    const handleOrConditionChange = (
        caseIndex: number,
        conditionIndex: number,
        field: 'value' | 'thenValue',
        value: string
    ) => {
        const updatedCases = [...orCases];
        updatedCases[caseIndex].conditions[conditionIndex][field] = value;
        setOrCases(updatedCases);
        setIsChanged(true);
    };

    const handleRemoveOrCondition = (
        caseIndex: number,
        conditionIndex: number
    ) => {
        const updatedCases = [...orCases];
        updatedCases[caseIndex].conditions.splice(conditionIndex, 1);
        setOrCases(updatedCases);
        setIsChanged(true);
    };

    const handleRemoveOrCase = (caseIndex: number) => {
        const updatedCases = [...orCases];
        updatedCases.splice(caseIndex, 1);
        setOrCases(updatedCases);
        setIsChanged(true);
    };

    const filterCurrentField = (fields: DocumentTypeFieldResponse[]) => {
        return fields.filter((field) => field.fieldId !== fieldId);
    };

    const validateCheckboxRange = (min: number, max: number): boolean => {
        if (min > max) {
            setCheckboxValidationError(
                'Minimum number of checkboxes cannot be greater than maximum'
            );
            return false;
        }
        setCheckboxValidationError('');
        return true;
    };

    interface ValidationTypeMapping {
        [key: string]: number;
    }

    const handleSave = async () => {
        // Validate checkbox settings before saving
        if (validationType === 'checkbox') {
            const min = validationConfig.checkboxMinimumSelected ?? 0;
            const max = validationConfig.checkboxMaximumSelected ?? 0;
            if (!validateCheckboxRange(min, max)) {
                showToast(
                    l('_error'),
                    'Minimum number of checkboxes cannot be greater than maximum',
                    'error'
                );
                return;
            }
        }

        try {
            let switchCases: SwitchCaseConfigRequest[] | null = null;
            if (validationType === 'and' || validationType === 'or') {
                const cases = validationType === 'and' ? andCases : orCases;
                switchCases = cases.flatMap((caseItem, caseIndex) => {
                    const dependentField = allFields.find(
                        (field) => field.fieldName === caseItem.field
                    );
                    const dependentFieldId = dependentField
                        ? dependentField.fieldId
                        : undefined;

                    const conditions = caseItem.conditions.map(
                        (
                            condition,
                            conditionIndex
                        ): SwitchCaseConfigRequest => ({
                            caseNumber: caseIndex + 1,
                            conditionNumber: conditionIndex + 1,
                            dependantMatchesRegex: condition.value || null,
                            dependentFieldId: dependentFieldId,
                            fieldMatchesRegex: condition.thenValue || null,
                        })
                    );

                    conditions.push({
                        caseNumber: caseIndex + 1,
                        conditionNumber: -1,
                        dependantMatchesRegex: null,
                        dependentFieldId: dependentFieldId,
                        fieldMatchesRegex: caseItem.elseValue || null,
                    });

                    return conditions;
                });
            }

            const updateRequest: UpdateValidationConfigRequest = {
                documentTypeFieldId: fieldId,
                validationTypeId: mapStringToValidationTypeId(validationType),
                simpleRegex: validationType === 'simple' ? simpleRegex : null,
                checkboxMaximumSelected:
                    validationType === 'checkbox'
                        ? validationConfig.checkboxMaximumSelected
                        : null,
                checkboxMinimumSelected:
                    validationType === 'checkbox'
                        ? validationConfig.checkboxMinimumSelected
                        : null,
                fromDate:
                    validationType === 'dateRange'
                        ? dateRangeStart?.toISOString()
                        : null,
                toDate:
                    validationType === 'dateRange'
                        ? dateRangeEnd?.toISOString()
                        : null,
                switchCases: switchCases,
            };

            await mutation.mutateAsync({ data: updateRequest });
            setIsChanged(false);
            showToast(l('_success'), l('_validationSettingsSaved'), 'success');
            navigate(-1);
        } catch (error: unknown) {
            // Try to extract backend error message and pass as AxiosError-like object
            let errorToShow: any = error;
            let backendMessage = '';
            if (
                error &&
                typeof error === 'object' &&
                'response' in error &&
                error.response &&
                typeof error.response === 'object' &&
                'data' in error.response &&
                error.response.data &&
                typeof error.response.data === 'string'
            ) {
                backendMessage = error.response.data;
                errorToShow = { message: backendMessage };
            } else if (
                error &&
                typeof error === 'object' &&
                'message' in error &&
                typeof error.message === 'string'
            ) {
                backendMessage = error.message;
                errorToShow = error;
            } else {
                backendMessage = 'An error occurred';
                errorToShow = { message: backendMessage };
            }

            // If backend error message contains 'is not a valid pattern', show a specific toast
            if (
                backendMessage &&
                backendMessage.includes('is not a valid pattern')
            ) {
                showToast('Invalid Regex Pattern', backendMessage, 'error');
            } else {
                showGenericErrorToast(errorToShow);
            }
        }
    };
    const mapStringToValidationTypeId = (type: string): number => {
        const validationTypeMapping: ValidationTypeMapping = {
            none: 0,
            simple: 1,
            dateRange: 4,
            checkbox: 5,
            and: 2,
            or: 3,
        };

        return validationTypeMapping[type] || 0;
    };

    interface ValidationTypeIdToStringMapping {
        [key: number]: string;
    }

    const mapValidationTypeIdToString = (typeId: number): string => {
        const validationTypeIdToStringMapping: ValidationTypeIdToStringMapping =
            {
                0: 'none',
                1: 'simple',
                4: 'dateRange',
                5: 'checkbox',
                2: 'and',
                3: 'or',
            };

        return validationTypeIdToStringMapping[typeId] || 'none';
    };

    const renderValidationContent = () => {
        switch (validationType) {
            case 'none':
                return (
                    <Panel className="panel-container">
                        <div className="panel-content">
                            <p
                                className="ap-typography-body-m"
                                style={{
                                    color: '#666',
                                    textAlign: 'center',
                                    padding: '20px',
                                }}
                            >
                                No validation will be required for this field.
                                The extracted value will be accepted without any
                                validation checks.
                            </p>
                        </div>
                    </Panel>
                );
            case 'simple':
                return (
                    <Panel className="panel-container">
                        <div className="panel-content">
                            <span className="regex-label">Should match</span>
                            <Input
                                required
                                value={simpleRegex}
                                onChange={(value: string) => {
                                    setIsChanged(true);
                                    setSimpleRegex(value);
                                }}
                            />
                        </div>
                    </Panel>
                );
            case 'dateRange':
                return (
                    <Panel className="panel-container">
                        <div className="date-range-container">
                            <div className="date-group">
                                <label
                                    htmlFor="startDate"
                                    className="regex-label"
                                >
                                    Should be after
                                </label>
                                <div className="date-input-group">
                                    <CalendarPicker
                                        required
                                        value={dateRangeStart}
                                        onChange={(date: Date | null) => {
                                            setDateRangeStart(date);
                                            setIsChanged(true);
                                        }}
                                        format="DD/MM/YYYY"
                                    />
                                </div>
                            </div>
                            <div className="date-group">
                                <label
                                    htmlFor="endDate"
                                    className="regex-label"
                                >
                                    Should be before
                                </label>
                                <div className="date-input-group">
                                    <CalendarPicker
                                        required
                                        value={dateRangeEnd}
                                        onChange={(date: Date | null) => {
                                            setDateRangeEnd(date);
                                            setIsChanged(true);
                                        }}
                                        format="DD/MM/YYYY"
                                    />
                                </div>
                            </div>
                        </div>
                    </Panel>
                );
            case 'checkbox':
                return (
                    <Panel className="panel-container">
                        <div className="date-range-container">
                            <div className="checkbox-group">
                                <label
                                    htmlFor="minCheckboxes"
                                    className="checkbox-label"
                                >
                                    Minimum number of checkboxes selected
                                </label>
                                <InputNumber
                                    required
                                    value={
                                        validationConfig.checkboxMinimumSelected ??
                                        0
                                    }
                                    min={0}
                                    max={
                                        validationConfig.checkboxMaximumSelected ??
                                        validationConfig.checkboxOptionCount ??
                                        0
                                    }
                                    onChange={(value) => {
                                        const newMin =
                                            typeof value === 'number'
                                                ? value
                                                : value
                                                ? parseInt(value, 10)
                                                : 0;
                                        setValidationConfig({
                                            ...validationConfig,
                                            checkboxMinimumSelected: newMin,
                                        });
                                        validateCheckboxRange(
                                            newMin,
                                            validationConfig.checkboxMaximumSelected ??
                                                0
                                        );
                                        setIsChanged(true);
                                    }}
                                    className={
                                        checkboxValidationError
                                            ? 'input-error'
                                            : ''
                                    }
                                />
                            </div>
                            <div className="checkbox-group">
                                <label
                                    htmlFor="maxCheckboxes"
                                    className="checkbox-label"
                                >
                                    Maximum number of checkboxes selected
                                </label>
                                <InputNumber
                                    required
                                    value={
                                        validationConfig.checkboxMaximumSelected ??
                                        0
                                    }
                                    min={
                                        validationConfig.checkboxMinimumSelected ??
                                        0
                                    }
                                    max={
                                        validationConfig.checkboxOptionCount ??
                                        0
                                    }
                                    onChange={(value) => {
                                        const newMax =
                                            typeof value === 'number'
                                                ? value
                                                : value
                                                ? parseInt(value, 10)
                                                : 0;
                                        setValidationConfig({
                                            ...validationConfig,
                                            checkboxMaximumSelected: newMax,
                                        });
                                        validateCheckboxRange(
                                            validationConfig.checkboxMinimumSelected ??
                                                0,
                                            newMax
                                        );
                                        setIsChanged(true);
                                    }}
                                    className={
                                        checkboxValidationError
                                            ? 'input-error'
                                            : ''
                                    }
                                />
                            </div>
                        </div>
                        {checkboxValidationError && (
                            <div className="error-message">
                                <span className="Appkit4-icon icon-exclamation-triangle-outline"></span>
                                <span>{checkboxValidationError}</span>
                            </div>
                        )}
                    </Panel>
                );
            case 'and':
            case 'or': {
                const cases = validationType === 'and' ? andCases : orCases;
                return (
                    <Panel className="panel-container">
                        <div className="add-case-button-container">
                            <Button
                                kind="secondary"
                                onClick={
                                    validationType === 'and'
                                        ? handleAddAndCase
                                        : handleAddOrCase
                                }
                            >
                                + Add case
                            </Button>
                        </div>
                        {cases.map((caseItem, caseIndex) => (
                            <div
                                key={caseIndex}
                                className={`${validationType}-case`}
                            >
                                <div className="case-header">
                                    <Combobox
                                        className="mailing-address"
                                        data={filterCurrentField(allFields).map(
                                            (field) => ({
                                                value: field.fieldName,
                                                label: field.fieldName,
                                            })
                                        )}
                                        placeholder="Select Field"
                                        value={caseItem.field}
                                        onSelect={(value) =>
                                            validationType === 'and'
                                                ? handleCaseFieldChange(
                                                      caseIndex,
                                                      value as string
                                                  )
                                                : handleOrCaseFieldChange(
                                                      caseIndex,
                                                      value as string
                                                  )
                                        }
                                    />
                                    <span
                                        onClick={() =>
                                            validationType === 'and'
                                                ? handleRemoveAndCase(caseIndex)
                                                : handleRemoveOrCase(caseIndex)
                                        }
                                        className="remove-case-icon"
                                    >
                                        Remove case
                                    </span>
                                </div>
                                {caseItem.conditions.map(
                                    (condition, conditionIndex) => (
                                        <div
                                            key={conditionIndex}
                                            className={`and-condition-group`}
                                        >
                                            <div className="match-row">
                                                <span className="match-label">
                                                    If {caseItem.field} matches
                                                </span>
                                                <Input
                                                    required
                                                    className="match-input"
                                                    value={condition.value}
                                                    onChange={(value: string) =>
                                                        validationType === 'and'
                                                            ? handleAndConditionChange(
                                                                  caseIndex,
                                                                  conditionIndex,
                                                                  'value',
                                                                  value
                                                              )
                                                            : handleOrConditionChange(
                                                                  caseIndex,
                                                                  conditionIndex,
                                                                  'value',
                                                                  value
                                                              )
                                                    }
                                                />
                                                <span className="match-label">
                                                    Then {fieldName} should
                                                    match
                                                </span>
                                                <Input
                                                    required
                                                    className="match-input"
                                                    value={condition.thenValue}
                                                    onChange={(value: string) =>
                                                        validationType === 'and'
                                                            ? handleAndConditionChange(
                                                                  caseIndex,
                                                                  conditionIndex,
                                                                  'thenValue',
                                                                  value
                                                              )
                                                            : handleOrConditionChange(
                                                                  caseIndex,
                                                                  conditionIndex,
                                                                  'thenValue',
                                                                  value
                                                              )
                                                    }
                                                />
                                                <span
                                                    onClick={() =>
                                                        validationType === 'and'
                                                            ? handleRemoveAndCondition(
                                                                  caseIndex,
                                                                  conditionIndex
                                                              )
                                                            : handleRemoveOrCondition(
                                                                  caseIndex,
                                                                  conditionIndex
                                                              )
                                                    }
                                                    className="Appkit4-icon icon-delete-outline"
                                                    style={{
                                                        marginTop: '12px',
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )
                                )}
                                <div className="last-condition-group">
                                    <Button
                                        kind="tertiary"
                                        onClick={() =>
                                            validationType === 'and'
                                                ? handleAddAndCondition(
                                                      caseIndex
                                                  )
                                                : handleAddOrCondition(
                                                      caseIndex
                                                  )
                                        }
                                        style={{
                                            marginBottom: '20px',
                                        }}
                                    >
                                        + Add Condition
                                    </Button>
                                    <div className="match-row">
                                        <span className="match-label">
                                            If {caseItem.field} matches anything
                                            else
                                        </span>
                                        <span> </span>
                                        <span className="match-label">
                                            Then {fieldName} should match
                                        </span>
                                        <Input
                                            required
                                            className="match-input"
                                            value={caseItem.elseValue}
                                            onChange={(value: string) => {
                                                const updatedCases = [...cases];
                                                updatedCases[
                                                    caseIndex
                                                ].elseValue = value;
                                                validationType === 'and'
                                                    ? setAndCases(updatedCases)
                                                    : setOrCases(updatedCases);
                                                setIsChanged(true);
                                            }}
                                        />
                                    </div>
                                </div>
                                {caseIndex < cases.length - 1 && (
                                    <div className="and-separator">
                                        {validationType.toUpperCase()}
                                    </div>
                                )}
                            </div>
                        ))}
                    </Panel>
                );
            }
            default:
                return null;
        }
    };

    if (isLoading)
        return <Loading loadingType="circular" indeterminate={true} />;

    return (
        <div className="doctype-settings">
            <div className="doctype-breadcrumb-container">
                <span
                    className="Appkit4-icon icon-arrow-left-outline"
                    role="button"
                    onClick={() => navigate(-1)}
                ></span>
                <Breadcrumbs
                    items={[
                        {
                            label: docType || '',
                            href: `${AppRoutes.FillOnlineDocTypeSettings}?docType=${docType}&docTypeId=${docTypeId}&processTypeId=${processTypeId}`,
                        },
                        {
                            label: fieldName || '',
                            href: '',
                        },
                    ]}
                />
            </div>
            <div className="validation-header">
                <h2 className="ap-typography-heading-m title">{fieldName}</h2>
                <div className="validation-type-container">
                    <span className="validation-type">Validation type</span>
                    <Combobox
                        data={getValidationTypes()}
                        value={mapValidationTypeIdToString(
                            validationData?.validationTypeId ?? 0
                        )}
                        placeholder=""
                        onSelect={(value) => setValidationType(value as string)}
                    />
                </div>
            </div>
            <div className="ap-simple-panel-container">
                {renderValidationContent()}
            </div>
            <div className="table-footer">
                <Button
                    kind="secondary"
                    disabled={
                        (validationType !== 'none' && !isChanged) ||
                        (validationType === 'checkbox' &&
                            !!checkboxValidationError)
                    }
                    onClick={handleSave}
                >
                    Save
                </Button>
            </div>
        </div>
    );
};

export default FillOnlineValidationSettings;
